import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { firstValueFrom } from "rxjs";
import { selectCustomer } from "src/app/auth/ngrx/customer.selectors";
import { FunctionService } from "src/app/supabase/services/function.service";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";

@Injectable({
  providedIn: "root",
})
export class GcalendarService {
  token: string;

  constructor(
    private functionService: FunctionService,
    private store: Store<any>
  ) {
  }

  async refreshGoogleToken() {
    var customer = await firstValueFrom(this.store.select(selectCustomer));

    if (customer && customer.google_refresh_token) {
      var tokenObj = await this.functionService.refreshGoogleToken(
        customer.google_refresh_token
      );
      this.token = tokenObj.access_token;
    }
  }

  async getGoogleCalendars() {
    await this.refreshGoogleToken();

    const url = "https://www.googleapis.com/calendar/v3/users/me/calendarList";
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    const data = await response.json();

    // exclude calendar Quick Drive
    return data.items.filter(
      (item) => item.summary !== "Quick Drive"
    );
  }

  async getGoogleEvents(calendarId: string) {
    await this.refreshGoogleToken();

    var startDate = new Date();
    var startDateCopy = new Date(startDate.setDate(startDate.getDate() - 60));
    var timeMin = startDateCopy.toISOString();

    var endDate = new Date();
    var endDateCopy = new Date(endDate.setDate(endDate.getDate() + 240));
    var timeMax = endDateCopy.toISOString();
    const encodedCalendarId = encodeURIComponent(calendarId);

    const url =
      "https://www.googleapis.com/calendar/v3/calendars/" +
      encodedCalendarId +
      "/events?timeMin=" +
      timeMin +
      "&timeMax=" +
      timeMax + 
      "&maxResults=2500";
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    const data = await response.json();

    const items: Record<string, any>[] = (
      data as Record<string, Record<string, any>[]>
    ).items;
  
    const scheduleData: Record<string, any>[] = [];
  
    if (items?.length > 0) {
      for (const event of items) {
        let when: string = event?.start?.dateTime as string;
        let start: string = event?.start?.dateTime as string;
        let end: string = event?.end?.dateTime as string;
        let recurrence: string = event?.recurrence;
        if (!when) {
          when = event?.start?.date as string;
          start = event?.start?.date as string;
          end = event?.end?.date as string;
        }
        if (recurrence) {
          recurrence = recurrence[0].split(":")[1];
        }
        scheduleData.push({
          Id: event?.id,
          GoogleId: event?.id,
          Subject: event?.summary,
          StartTime: new Date(start),
          EndTime: !event?.start?.dateTime ? new Date(start) : new Date(end),
          IsAllDay: !event?.start?.dateTime,
          Location: event?.location,
          Description: event?.description,
          IsReadOnly: true,
          RecurrenceRule: recurrence,
          ResourceId: calendarId,
        });
      }
    }

    return scheduleData as AppointmentDto[];
  }

  async findChangedGoogleEvents(
    calendarId: string,
    localEvents: AppointmentDto[]
  ) {
    // check if any google event has been changed (deleted or updated)
    const googleEvents = await this.getGoogleEvents(calendarId);
    const googleEventIds = googleEvents.map((event) => event.GoogleId);

    // deletedEvents are events that exist in localEvents but not in googleEvents
    const deletedEvents = localEvents.filter(
      (event) => !googleEventIds.includes(event.GoogleId)
    );
    const updatedEvents = localEvents.filter((event) =>
      googleEvents.some(
        (googleEvent) =>
          googleEvent.GoogleId === event.GoogleId &&
          (googleEvent.StartTime !== event.StartTime || googleEvent.EndTime !== event.EndTime || googleEvent.Location !== event.Location || googleEvent.Description !== event.Description)
    ))
    
    return { deletedEvents, updatedEvents };
  }

  async createGoogleCalendar(calendarName: string) {
    await this.refreshGoogleToken();
    const url = "https://www.googleapis.com/calendar/v3/calendars";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        summary: calendarName
      }),
    });
    const data = await response.json();
    return data;
  }

  async createGoogleEvent(calendarId: string, event: any) {
    await this.refreshGoogleToken();
    const url =
      "https://www.googleapis.com/calendar/v3/calendars/" +
      calendarId +
      "/events";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    });
    const data = await response.json();
    return data;
  }

  async updateGoogleEvent(calendarId: string, eventId: string, event: any) {
    await this.refreshGoogleToken();
    const url =
      "https://www.googleapis.com/calendar/v3/calendars/" +
      calendarId +
      "/events/" +
      eventId;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    });
    const data = await response.json();
    return data;
  }

  async deleteGoogleEvent(calendarId: string, eventId: string) {
    await this.refreshGoogleToken();
    const url =
      "https://www.googleapis.com/calendar/v3/calendars/" +
      calendarId +
      "/events/" +
      eventId;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    return data;
  }
}
